import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const VALUES = [
  { title: 'Authenticity', proof: 'Real work, real numbers. No stock, no jargon.' },
  { title: 'Fast Delivery', proof: 'Avg. project kickoff to first demo: under 1 week.' },
  { title: 'Client Happiness', proof: '96% of clients return for a second project.' },
  { title: 'Communication', proof: 'Avg. first response: under 2 hours.' },
  { title: 'Consistency', proof: 'One design & motion system, every screen.' },
];

const MARQUEE = ['Authenticity', 'Fast Delivery', 'Client Happiness', 'Communication', 'Consistency'];

export default function Values({ reduced }) {
  const sectionRef = useRef(null);
  const marqueeRef = useRef(null);

  useEffect(() => {
    if (reduced || !marqueeRef.current) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        marqueeRef.current,
        { xPercent: 0 },
        {
          xPercent: -50,
          ease: 'none',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 0.6,
          },
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, [reduced]);

  return (
    <section
      ref={sectionRef}
      id="values"
      className="relative py-24 md:py-40 bg-obsidian text-bone overflow-hidden"
      data-testid="values-section"
    >
      <div className="mb-16 md:mb-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <span className="font-sans text-xs uppercase tracking-[0.3em] text-bone/40">Why us</span>
        </div>

        <div className="mt-8 relative w-full overflow-hidden">
          <div ref={marqueeRef} className={`flex whitespace-nowrap w-max ${reduced ? '' : ''}`}>
            {[...MARQUEE, ...MARQUEE].map((v, i) => (
              <span
                key={i}
                className="serif italic text-[14vw] md:text-[10vw] leading-none tracking-tighter mx-8 text-bone/90"
              >
                {v} <span className="text-signal not-italic">✳</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-6 md:px-12 grid md:grid-cols-5 gap-8">
        {VALUES.map((v, i) => (
          <motion.div
            key={v.title}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, delay: i * 0.08 }}
            className="border-t border-bone/15 pt-6"
            data-testid={`value-${i}`}
          >
            <div className="font-sans text-xs tracking-[0.2em] text-signal mb-3">0{i + 1}</div>
            <h3 className="serif text-2xl tracking-tight mb-4">{v.title}</h3>
            <p className="font-sans text-sm text-bone/60 leading-relaxed">{v.proof}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
